import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, finalize, Subscription, take } from 'rxjs';
import { OverloadMemoService } from 'src/app/services/api/overload-memo/overload-memo.service';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import {
  OverloadMemoCertificationStatus,
  OverloadMemoDepartmentGroupSnapshot,
  OverloadMemoModel,
  OverloadMemoStatus,
} from 'src/app/services/models/overload-memo/overload-memo.model';
import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';
import { SharedModule } from 'src/shared/shared.module';
import {
  buildAcademicTermNameOptions,
  buildAcademicYearOptions,
  defaultAcademicTerm,
  findAcademicTerm,
  toBuddhistYear,
} from 'src/utils/academic-term-options';

// เลขที่เอกสารตั้งต้น (คำนำหน้าหนังสือคณะวิศวกรรมศาสตร์) — admin เติมเลขที่รันต่อท้าย "/".
// ใช้ทั้งตอน init ฟอร์ม และตอนรีเซ็ตฟอร์มเมื่อสลับไปเทอมที่ยังไม่มีการสร้างเอกสาร.
const DEFAULT_DOCUMENT_NUMBER = 'อว 660301.3.1.4/';

@Component({
  selector: 'app-overload-memo',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    SharedModule,
  ],
  templateUrl: './overload-memo.component.html',
  styleUrl: './overload-memo.component.scss',
})
export class OverloadMemoComponent implements OnInit, OnDestroy {
  form = inject(FormBuilder).group({
    academicTermId: [null as number | null, Validators.required],
    academicTerm: [null as string | null],
    academicYear: [null as string | null],
    documentNumber: [DEFAULT_DOCUMENT_NUMBER, Validators.required],
    effectiveDate: [this.todayIsoDate(), Validators.required],
  });

  academicTermNameOptions: string[] = [];
  academicYearOptions: string[] = [];
  memo = signal<OverloadMemoModel | null>(null);
  loading = signal(false);
  submitting = signal(false);
  submitError = signal<string | null>(null);
  pdfLoading = signal(false);
  pdfError = signal(false);
  pdfPreviewUrl = signal<SafeResourceUrl | null>(null);
  // True when the form has un-previewed edits. "ส่งคำขอ" is disabled until the
  // admin regenerates so we always send exactly the previewed PDF (WYSIWYG).
  previewDirty = signal(false);

  private academicTerms: AcademicTermYearModel[] = [];
  private rawPdfUrl: string | null = null;
  private readonly subscriptions = new Subscription();
  private readonly overloadMemoService = inject(OverloadMemoService);
  private readonly studyPeriodService = inject(StudyPeriodService);
  private readonly sanitizer = inject(DomSanitizer);

  ngOnInit(): void {
    this.loadOptions();
    this.subscriptions.add(
      this.form.controls.academicTermId.valueChanges
        .pipe(
          debounceTime(250),
          distinctUntilChanged(),
        )
        .subscribe(() => this.loadExistingMemo()),
    );
    this.subscriptions.add(
      this.form.controls.academicYear.valueChanges.subscribe(() => this.onAcademicYearChanged()),
    );
    this.subscriptions.add(
      this.form.controls.academicTerm.valueChanges.subscribe(() => this.syncAcademicTermId()),
    );
    this.subscriptions.add(
      this.form.controls.documentNumber.valueChanges.subscribe(() => this.markPreviewDirty()),
    );
    this.subscriptions.add(
      this.form.controls.effectiveDate.valueChanges.subscribe(() => this.markPreviewDirty()),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.revokePdfUrl();
  }

  onSubmit(): void {
    if (this.form.invalid || this.submitting() || !this.canSubmit()) {
      this.form.markAllAsTouched();
      return;
    }

    const selectedMemo = this.memo();
    const payload = this.buildPayload();
    this.submitError.set(null);
    this.submitting.set(true);
    // canSubmit() guarantees the memo (if any) is editable (draft / revision_requested),
    // so an existing memo is regenerated (preview only) and a new one is created.
    const request = selectedMemo == null
      ? this.overloadMemoService.createOverloadMemo(payload)
      : this.overloadMemoService.regenerateOverloadMemo(selectedMemo.id, payload);

    request.pipe(
      finalize(() => this.submitting.set(false)),
    ).subscribe({
      next: (memo) => {
        this.applyMemo(memo);
      },
      error: (err) => {
        this.submitError.set(this.extractErrorMessage(err));
        this.pdfError.set(true);
      },
    });
  }

  onSendRequest(): void {
    const selectedMemo = this.memo();
    if (selectedMemo == null || this.submitting() || !this.canSendRequest()) {
      return;
    }

    this.submitError.set(null);
    this.submitting.set(true);
    this.overloadMemoService.submitOverloadMemo(selectedMemo.id)
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: (memo) => {
          this.applyMemo(memo);
        },
        error: (err) => {
          this.submitError.set(this.extractErrorMessage(err));
        },
      });
  }

  onDownloadPdf(): void {
    const selectedMemo = this.memo();
    if (selectedMemo == null || !this.canDownload()) {
      return;
    }

    this.overloadMemoService.downloadOverloadMemo(selectedMemo.id)
      .pipe(take(1))
      .subscribe((blob) => this.downloadBlob(blob, `overload_memo_${selectedMemo.id}.pdf`));
  }

  getRosterGroups(): OverloadMemoDepartmentGroupSnapshot[] {
    return this.memo()?.entries_snapshot ?? [];
  }

  entriesCount(memo: OverloadMemoModel | null = this.memo()): number {
    return memo?.entries_count ?? this.getRosterGroups().reduce((total, group) => (
      total + group.teachers.length
    ), 0);
  }

  certificationStatusLabel(status?: OverloadMemoCertificationStatus | null): string {
    switch (status) {
      case 'certified':
        return 'รับรองแล้ว';
      case 'rejected':
        return 'ส่งกลับแก้ไข';
      case 'pending':
      case null:
      case undefined:
      default:
        return 'รอรับรอง';
    }
  }

  certificationStatusClass(status?: OverloadMemoCertificationStatus | null): string {
    switch (status) {
      case 'certified':
        return 'overload-memo-cert-certified';
      case 'rejected':
        return 'overload-memo-cert-rejected';
      case 'pending':
      case null:
      case undefined:
      default:
        return 'overload-memo-cert-pending';
    }
  }

  // Derives the หัวหน้างานการศึกษา certification badge from the memo's overall status.
  // The education head certifies first; once the memo advances past their step
  // (pending_vice_dean / certified) their part is done. Any rejection (head or vice
  // dean) paints the head chip as ส่งกลับแก้ไข.
  educationHeadCertificationStatus(): OverloadMemoCertificationStatus {
    const memo = this.memo();
    switch (memo?.status_code) {
      case 'certified':
      case 'pending_vice_dean':
        return 'certified';
      case 'revision_requested':
        return 'rejected';
      case 'draft':
      case 'pending_education_head':
      case null:
      case undefined:
      default:
        return 'pending';
    }
  }

  // Derives the vice dean (รองคณบดีฝ่ายวิชาการ) certification badge from the memo's
  // overall status. Certifying as vice dean IS the terminal "certified" step, so we
  // only paint "ตีกลับ" when the latest rejection actually came from the vice dean —
  // an education-head rejection leaves the vice dean still pending.
  viceDeanCertificationStatus(): OverloadMemoCertificationStatus {
    const memo = this.memo();
    switch (memo?.status_code) {
      case 'certified':
        return 'certified';
      case 'revision_requested':
        return memo.last_rejection?.actor_role_code === 'vice_dean_academic'
          ? 'rejected'
          : 'pending';
      case 'draft':
      case 'pending_education_head':
      case 'pending_vice_dean':
      case null:
      case undefined:
      default:
        return 'pending';
    }
  }

  canSubmit(): boolean {
    return this.isEditable(this.memo());
  }

  canSendRequest(): boolean {
    const selectedMemo = this.memo();
    return selectedMemo != null
      && this.isEditable(selectedMemo)
      && selectedMemo.active_pdf_document_id != null
      && !this.previewDirty();
  }

  canDownload(): boolean {
    return this.memo()?.status_code === 'certified';
  }

  submitLabel(): string {
    return 'สร้างเอกสาร';
  }

  statusBadgeClass(status?: OverloadMemoStatus | null): string {
    switch (status) {
      case 'certified':
        return 'overload-memo-status-certified';
      case 'revision_requested':
        return 'overload-memo-status-revision';
      case 'pending_vice_dean':
        return 'overload-memo-status-review';
      case 'draft':
      case 'pending_education_head':
      case null:
      case undefined:
        return 'overload-memo-status-pending';
      default:
        return 'overload-memo-status-pending';
    }
  }

  private isEditable(memo: OverloadMemoModel | null): boolean {
    return memo == null
      || memo.status_code === 'draft'
      || memo.status_code === 'revision_requested';
  }

  private markPreviewDirty(): void {
    if (this.memo() != null) {
      this.previewDirty.set(true);
    }
  }

  private loadOptions(): void {
    this.loading.set(true);
    this.studyPeriodService.getAcademicTermYears().pipe(
      take(1),
      finalize(() => this.loading.set(false)),
    ).subscribe({
      next: (terms) => {
        this.academicTerms = terms;
        this.academicYearOptions = buildAcademicYearOptions(terms);
        const defaultTerm = defaultAcademicTerm(terms);
        const defaultYear = defaultTerm != null ? String(toBuddhistYear(defaultTerm.year)) : null;
        this.academicTermNameOptions = buildAcademicTermNameOptions(terms, defaultYear);
        this.form.patchValue({
          academicYear: defaultYear,
          academicTerm: defaultTerm != null ? String(defaultTerm.name).trim() : null,
          academicTermId: defaultTerm?.id ?? null,
        }, { emitEvent: false });
        this.loadExistingMemo();
      },
      error: (err) => console.error(err),
    });
  }

  private loadExistingMemo(): void {
    const { academicTermId } = this.form.getRawValue();
    if (academicTermId == null) {
      this.applyMemo(null);
      return;
    }

    this.submitError.set(null);
    this.loading.set(true);
    this.overloadMemoService.getOverloadMemos({
      academic_term_id: academicTermId,
      page: 1,
      limit: 1,
    }).pipe(
      finalize(() => this.loading.set(false)),
    ).subscribe({
      next: (response) => {
        this.applyMemo(response.data[0] ?? null);
      },
      error: (err) => {
        console.error(err);
        this.applyMemo(null);
      },
    });
  }

  private applyMemo(memo: OverloadMemoModel | null): void {
    this.memo.set(memo);
    this.previewDirty.set(false);

    // An existing memo shows its own saved values; a term with no memo yet resets
    // to the defaults (prefilled เลขที่เอกสาร prefix + today) so the previously
    // selected term's values don't carry over when switching terms.
    this.form.patchValue({
      documentNumber: memo?.document_number ?? DEFAULT_DOCUMENT_NUMBER,
      effectiveDate: memo?.effective_date ?? this.todayIsoDate(),
    }, { emitEvent: false });

    this.applyEditableState(memo);

    if (memo?.active_pdf_document_id != null) {
      this.loadPdfPreview(memo.id);
    } else {
      this.revokePdfUrl();
      this.pdfPreviewUrl.set(null);
    }
  }

  // Lock เลขเอกสาร / วันที่ once the memo has been sent (any status past the
  // editable draft / revision_requested states); the admin can still switch terms.
  private applyEditableState(memo: OverloadMemoModel | null): void {
    const editable = this.isEditable(memo);
    [this.form.controls.documentNumber, this.form.controls.effectiveDate].forEach((control) => {
      if (editable) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    });
  }

  private loadPdfPreview(id: number): void {
    this.pdfLoading.set(true);
    this.pdfError.set(false);
    this.overloadMemoService.downloadOverloadMemo(id)
      .pipe(finalize(() => this.pdfLoading.set(false)))
      .subscribe({
        next: (blob) => {
          this.revokePdfUrl();
          this.rawPdfUrl = window.URL.createObjectURL(blob);
          this.pdfPreviewUrl.set(
            this.sanitizer.bypassSecurityTrustResourceUrl(`${this.rawPdfUrl}#toolbar=0&navpanes=0`),
          );
        },
        error: (err) => {
          console.error(err);
          this.revokePdfUrl();
          this.pdfPreviewUrl.set(null);
          this.pdfError.set(true);
        },
      });
  }

  private buildPayload() {
    const value = this.form.getRawValue();
    return {
      overload_memo: {
        academic_term_id: value.academicTermId,
        document_number: String(value.documentNumber ?? '').trim(),
        effective_date: value.effectiveDate,
      },
    };
  }

  private revokePdfUrl(): void {
    if (this.rawPdfUrl != null) {
      window.URL.revokeObjectURL(this.rawPdfUrl);
      this.rawPdfUrl = null;
    }
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private todayIsoDate(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private onAcademicYearChanged(): void {
    const { academicYear, academicTerm } = this.form.getRawValue();
    this.academicTermNameOptions = buildAcademicTermNameOptions(this.academicTerms, academicYear);
    const nextTerm = academicTerm != null && this.academicTermNameOptions.includes(academicTerm)
      ? academicTerm
      : this.academicTermNameOptions[0] ?? null;
    this.form.patchValue({ academicTerm: nextTerm }, { emitEvent: false });
    this.syncAcademicTermId();
  }

  private syncAcademicTermId(): void {
    const { academicTerm, academicYear } = this.form.getRawValue();
    const resolved = findAcademicTerm(this.academicTerms, academicTerm, academicYear);
    this.form.patchValue({ academicTermId: resolved?.id ?? null });
  }

  private extractErrorMessage(err: unknown): string {
    if (err !== null && typeof err === 'object' && 'error' in err) {
      const error = (err as { error?: unknown }).error;
      if (error !== null && typeof error === 'object' && 'errors' in error) {
        const errors = (error as { errors?: unknown }).errors;
        if (Array.isArray(errors) && errors.length > 0) {
          return String(errors[0]);
        }
        if (typeof errors === 'string') {
          return errors;
        }
      }
    }

    return 'ไม่สามารถดำเนินการได้';
  }
}
